import { getOr, isNull } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Redirect, RouteComponentProps } from 'react-router';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { useFetchStarted } from '../../../hooks/useFetchStarted';
import { ANALYSIS_MARGIN, NAV_BAR, PAGE_MARGIN_PADDING_BORDER, PAGE_VERTICAL_MARGIN_PADDING_BORDER, useSplitView } from '../../../hooks/useSplitView';
import { useWindowResize } from '../../../hooks/useWindowResize';
import { fetchMLReadyAgreementTypeIdsStarted } from '../../admin/documents/store';
import { ClientFeaturePermission, fetchAllBasicUsersStarted } from '../../admin/users/store';
import { AllInstancesWrapper } from '../../datasets/instances/AllInstancesWrapper';
import { AmendmentMLDataModal } from '../../datasets/instances/ml-modal/AmendmentMLDataModal';
import { MLDataModal } from '../../datasets/instances/ml-modal/MLDataModal';
import { fetchECSDatasetIdStarted, getAmendmentMLDataModalOpen, getDatasetInstance, getInstanceContainsMLData, getIsOpeningDocumentAndInstance, getMLData, getMLDataModalOpen, getMLDataUnconfirmed, resetDatasetInstance, toggleMLDataModal } from '../../datasets/instances/store';
import { Analysis, Tabs } from '../../shared/analysis/Analysis';
import { AnalysisSideMenu } from '../../shared/analysis/AnalysisSideMenu';
import { ControlBarView, DocumentViewerType, resetPdfText, setControlBarView } from '../document-viewer/store';
import { getDocumentAnalysisSelectedPage, getDocumentAnalysisView, getDocumentInView, getDocumentNettingAnalysisModalOpen, getDocumentUrl, getIsOpening, getOriginalDocument, getSecondaryDocument, getSelectedSupportDocument, getSupportDocuments, openDocumentAndInstance, resetDocumentInstance, setDocumentAnalysisSelectedPage, toggleDocumentAnalysisView, toggleDocumentInView, toggleDocumentNettingAnalysisModal } from '../my-documents/store';
import styles from './DocumentAnalysis.module.scss';
import { AnalysisView, DocumentType, NETTING_ENGINE_DOCUMENT_NAME_IDS, SelectedDocument } from '../store/types';
import { getClientHasFeaturePermission } from '../../auth/login/store/selectors';
import { getNettingDetermination } from '../../opinions/netting-engine/store/utils';
import { fetchAllNettingEntitiesStarted } from '../../admin/entity/store/actions';
import { resetEngineDataState } from '../../opinions/netting-engine/store';

interface AnalysisRouteParams {
    documentId: string | undefined;
    datasetInstanceId: string | undefined;
}

export const DocumentAnalysis: React.FC<RouteComponentProps<AnalysisRouteParams>> = ({ match }) => {
    const [currentDocumentId, setCurrentDocumentId] = useState<string>('');
    useFetchStarted([fetchECSDatasetIdStarted(), fetchMLReadyAgreementTypeIdsStarted(), fetchAllBasicUsersStarted(), fetchAllNettingEntitiesStarted()]);
    const [screenWidth, screenHeight] = useWindowResize();
    const datasetInstance = useAppSelector(getDatasetInstance);
    const originalDocument = useAppSelector(getOriginalDocument);
    const dispatch = useAppDispatch();
    const documentUrl = useAppSelector(getDocumentUrl);
    const secondaryDocument = useAppSelector(getSecondaryDocument);
    const supportDocument = useAppSelector(getSelectedSupportDocument);
    const documentInView = useAppSelector(getDocumentInView);
    const supportDocuments = useAppSelector(getSupportDocuments);
    const isOpeningDocument = useAppSelector(getIsOpening);
    const isOpeningDocumentAndInstance = useAppSelector(getIsOpeningDocumentAndInstance);
    const documentAnalysisView = useAppSelector(getDocumentAnalysisView);
    const mlDataModalOpen = useAppSelector(getMLDataModalOpen);
    const showMLDataOption = useAppSelector(getInstanceContainsMLData);
    const amendmentMLDataModalOpen = useAppSelector(getAmendmentMLDataModalOpen);
    const mlDataModalDisabled = useAppSelector(getMLDataUnconfirmed);
    const selectedPage = useAppSelector(getDocumentAnalysisSelectedPage);
    const mlData = useAppSelector(getMLData);
    const documentNettingAnalysisModalOpen = useAppSelector(getDocumentNettingAnalysisModalOpen);
    const [pageWidth, pageHeight] = useSplitView(documentAnalysisView, PAGE_MARGIN_PADDING_BORDER, PAGE_VERTICAL_MARGIN_PADDING_BORDER);
    const scheduleStartPage = useMemo(() => !isNull(originalDocument) && originalDocument.scheduleStartPage || 1, [originalDocument]);
    const hasOpinionModule = useAppSelector(getClientHasFeaturePermission([ClientFeaturePermission.OPINION_MODULE]));

    const showMlDataOptionIcon = useMemo(() => showMLDataOption && !amendmentMLDataModalOpen, [showMLDataOption, amendmentMLDataModalOpen]);
    const showMLLatestIndicator = useMemo(() => !isNull(mlData) && mlData.version < mlData.latestVersion, [mlData]);

    const isOpening = useMemo(() => isOpeningDocument || isOpeningDocumentAndInstance, [isOpeningDocument, isOpeningDocumentAndInstance]);

    const { showNettingAnalysisOption, nettingAnalysisColour } = useMemo(() => {
        if (!isNull(originalDocument) && !isNull(originalDocument.nettingSummary) && !isNull(originalDocument.documentNameId) && NETTING_ENGINE_DOCUMENT_NAME_IDS.includes(originalDocument.documentNameId)) {
            const { colour } = getNettingDetermination(originalDocument.nettingSummary!, originalDocument, hasOpinionModule);
            return { showNettingAnalysisOption: true, nettingAnalysisColour: colour };
        }
        return { showNettingAnalysisOption: false, nettingAnalysisColour: undefined };
    }, [originalDocument, hasOpinionModule]);

    const toggleNettingAnalysis = useCallback(() => dispatch(toggleDocumentNettingAnalysisModal(!documentNettingAnalysisModalOpen)), [dispatch, documentNettingAnalysisModalOpen]);

    const selectedDocument = useMemo(() => {
        switch (documentInView) {
            case SelectedDocument.SUPPORT:
                return supportDocument;
            case SelectedDocument.SECONDARY:
                return secondaryDocument;
            case SelectedDocument.ORIGINAL:
            default:
                return originalDocument;

        }
    }, [documentInView, supportDocument, secondaryDocument, originalDocument]);

    const showScheduleStartPage = useMemo(() => documentInView === SelectedDocument.ORIGINAL, [documentInView]);

    const toggleSelectedDocument = useCallback((selectedDocument: SelectedDocument) => {
        if (selectedDocument !== documentInView) {
            dispatch(toggleDocumentInView(selectedDocument));
            const controlBarView = selectedDocument === SelectedDocument.SUPPORT ? ControlBarView.SUPPORT_DOCUMENTS : ControlBarView.SEARCH;
            dispatch(setControlBarView(controlBarView));
        }
    }, [dispatch, documentInView]);
    const toggleAnalysisView = useCallback((view: AnalysisView) => { view !== documentAnalysisView && dispatch(toggleDocumentAnalysisView(view)); }, [dispatch, documentAnalysisView]);
    const toggleMLModal = useCallback(() => { dispatch(toggleMLDataModal(!mlDataModalOpen)); }, [dispatch, mlDataModalOpen]);

    const setSelectedPage = useCallback((page: number | null) => dispatch(setDocumentAnalysisSelectedPage(page)), [dispatch]);

    const tabOptions = useMemo<Tabs[]>(() => {
        let tabs = [
            {
                title: getOr('', 'documentDescription', originalDocument),
                onClick: () => toggleSelectedDocument(SelectedDocument.ORIGINAL),
                selected: documentInView === SelectedDocument.ORIGINAL,
                type: originalDocument?.documentType || undefined
            },
            {
                title: getOr('', 'documentDescription', secondaryDocument),
                onClick: () => toggleSelectedDocument(SelectedDocument.SECONDARY),
                selected: documentInView === SelectedDocument.SECONDARY,
                disabled: !secondaryDocument,
                type: secondaryDocument?.documentType || undefined
            }
        ];

        if (supportDocuments.length) {
            tabs.push({
                title: `Support Documents (${supportDocuments.length})`,
                onClick: () => toggleSelectedDocument(SelectedDocument.SUPPORT),
                selected: documentInView === SelectedDocument.SUPPORT,
                type: DocumentType.SUPPORT
            });
        }

        return tabs;
    }, [toggleSelectedDocument, documentInView, originalDocument, secondaryDocument, supportDocuments]);

    const { params } = match;

    useEffect(() => {
        if ((!datasetInstance && !originalDocument && params.documentId && currentDocumentId === '') || ((params.documentId && currentDocumentId.length > 0) && params.documentId !== currentDocumentId)) {
            setCurrentDocumentId(params.documentId);
            dispatch(openDocumentAndInstance(params.documentId, params.datasetInstanceId));
        }
    }, [params, datasetInstance, dispatch, originalDocument, currentDocumentId]);

    useEffect(() => () => {
        dispatch(resetPdfText());
        dispatch(resetDatasetInstance());
        dispatch(resetDocumentInstance());
        dispatch(resetEngineDataState());
    }, [dispatch]);

    const [width] = useSplitView(documentAnalysisView, PAGE_MARGIN_PADDING_BORDER + 2, PAGE_VERTICAL_MARGIN_PADDING_BORDER);

    if (!params.documentId) {
        return (
            <Redirect to='/documents' />
        );
    }

    const analysisWrapperDimensions = { height: `${screenHeight - NAV_BAR - ANALYSIS_MARGIN}px`, width: `${screenWidth}px` };
    const pageDimensions = { height: `${pageHeight}px`, width: `${pageWidth}px` };

    return (
        <div className={styles.analysisWrapper} style={analysisWrapperDimensions}>
            <AnalysisSideMenu
                toggleAnalysisView={toggleAnalysisView}
                currentView={documentAnalysisView}
                showMLDataOption={showMlDataOptionIcon}
                toggleMLModal={toggleMLModal}
                mlDataModalDisabled={mlDataModalDisabled}
                showMLLatestIndicator={showMLLatestIndicator}
                showChatbotIcon
                showNettingAnalysisOption={showNettingAnalysisOption}
                nettingAnalysisColour={nettingAnalysisColour}
                toggleNettingAnalysis={toggleNettingAnalysis}
            />
            <div className={styles.splitWrapper}>
                {documentAnalysisView !== AnalysisView.DATASET &&
                    <div className={styles.documentAnalysisWrapper} style={pageDimensions}>
                        <Analysis
                            selected={selectedDocument}
                            url={documentUrl}
                            documentInView={documentInView}
                            isOpening={isOpening}
                            tabOptions={tabOptions}
                            documentAnalysisView={documentAnalysisView}
                            selectedPage={selectedPage}
                            showScheduleStartPage={showScheduleStartPage}
                            scheduleStartPage={scheduleStartPage}
                            setSelectedPage={setSelectedPage}
                            width={width}
                            type={DocumentViewerType.DOCUMENT_ANALYSIS}
                        />
                    </div>
                }
                {documentAnalysisView !== AnalysisView.DOCUMENT &&
                    <div className={styles.datasetAnalysisWrapper} style={pageDimensions}>
                        <AllInstancesWrapper />
                    </div>
                }
            </div>
            <MLDataModal />
            <AmendmentMLDataModal />
        </div>
    );
};
